'use client';

import { useCallback, useState } from 'react';
import { useLocale } from 'next-intl';
import { useQueryClient } from '@tanstack/react-query';
import type { QueryClient } from '@tanstack/react-query';
import { purchaseService } from '@/features/payment/api/purchase';
import {
  isPaddleClientConfigured,
  openCheckoutForDescriptor,
  toPaddleLocale,
} from '@/shared/lib/paddle';
import { useUser } from '@/features/auth/model/auth.selectors';
import { readGeoCountry } from '@/shared/lib/geo-country';
import { cacheKeys } from '@/shared/lib/cache-invalidation';
import type { CheckoutDescriptor } from '@/types/checkout';

/**
 * Refresh every ownership/entitlement surface after a Pass or subscription
 * purchase. The webhook owns the grant; this only accelerates the local flip so
 * the storefront swaps the buy CTA for the owned state without a manual reload.
 * WS `user/updated` + the reconcile layers remain the authoritative backstop.
 */
function invalidateAfterEntitlementPurchase(queryClient: QueryClient): void {
  queryClient.invalidateQueries({ queryKey: cacheKeys.subscriptions.current });
  queryClient.invalidateQueries({ queryKey: cacheKeys.subscriptions.list });
  queryClient.invalidateQueries({ queryKey: cacheKeys.subscriptions.usage });
  queryClient.invalidateQueries({ queryKey: cacheKeys.auth.user });
  queryClient.invalidateQueries({ queryKey: cacheKeys.profile });
  queryClient.invalidateQueries({ queryKey: cacheKeys.payments });
  // The Pass catalog (usePasses) carries per-user is_owned; drop it so the
  // purchased card flips to its owned state.
  queryClient.invalidateQueries({ queryKey: cacheKeys.passes.all });
  // The store catalog's pack cards carry `has_active_pack` / `is_additional` /
  // `is_owned` — a Pro Plan purchase is a global unlock that flips them on
  // every building, so refresh the whole catalog too.
  queryClient.invalidateQueries({ queryKey: cacheKeys.storeCatalog.all });
  // Pro Plan is a GLOBAL unlock: every building's `pack.is_unlimited` flips
  // server-side with no per-project purchase row. The in-building nav glyph
  // (crown vs token balance) reads the cached project's pack state, so without
  // this the user keeps seeing the bare token number instead of the crown
  // until something else happens to refetch the project.
  queryClient.invalidateQueries({ queryKey: cacheKeys.projects.all });
}

/**
 * Refresh after a token-package purchase: the balance scalar plus the payment
 * history. The Paddle webhook credits the ledger; this only accelerates the
 * local flip (WS `user/updated` remains the authoritative backstop).
 */
function invalidateAfterTokenPurchase(queryClient: QueryClient): void {
  queryClient.invalidateQueries({ queryKey: cacheKeys.balance });
  queryClient.invalidateQueries({ queryKey: cacheKeys.payments });
  // The token-package catalog carries per-user pricing/state; refresh it so
  // the bought package reflects the new balance-driven copy without waiting
  // out the 5min staleTime.
  queryClient.invalidateQueries({ queryKey: cacheKeys.tokenPackages.all });
}

export interface ProductCheckoutCallbacks {
  /** checkout.completed — the host flips to its success/owned state. */
  onPurchased?: () => void;
  /** checkout.closed (purchase OR cancel) — the host restores its resting state. */
  onClosed?: () => void;
}

/**
 * Account-level checkout (Tokens Pro Pass = `pass`; Pro Plan =
 * `subscription`; token packages = `token`). Generalizes
 * {@link usePackCheckout} for products that
 * are NOT building-bound: it mints the correlation intent, opens the checkout
 * via the server-provided `descriptor` (provider-agnostic — Paddle today), and
 * refreshes ownership on completion. The webhook remains the source of truth.
 *
 * `available` is false when the API shipped no `descriptor` (country not served
 * on a web rail) or the Paddle client isn't configured — the CTA then shows the
 * server `web_payment_notice` instead of a dead button.
 */
export function useProductCheckout({
  descriptor,
  kind,
  sku,
  callbacks,
}: {
  descriptor: CheckoutDescriptor | null | undefined;
  kind: 'pass' | 'subscription' | 'token';
  sku: string;
  callbacks?: ProductCheckoutCallbacks;
}) {
  const queryClient = useQueryClient();
  const user = useUser();
  const locale = useLocale();
  const [isLaunching, setIsLaunching] = useState(false);
  const onPurchased = callbacks?.onPurchased;
  const onClosed = callbacks?.onClosed;

  const available = !!descriptor && isPaddleClientConfigured();

  const startCheckout = useCallback(async () => {
    if (!descriptor) {
      return;
    }

    setIsLaunching(true);
    try {
      const intent =
        kind === 'subscription'
          ? await purchaseService.createSubscriptionIntent(sku)
          : kind === 'token'
            ? await purchaseService.createTokenIntent(sku)
            : await purchaseService.createPassIntent(sku);

      await openCheckoutForDescriptor(descriptor, {
        kind,
        correlationToken: intent.correlation_token,
        // Prefill buyer email (always known — logged-in) + live geo country so
        // Paddle can skip its "Your details" step. Paddle still owns final tax.
        customerEmail: user?.email || undefined,
        countryCode: readGeoCountry(),
        locale: toPaddleLocale(locale),
        onCompleted: () => {
          if (kind === 'token') {
            invalidateAfterTokenPurchase(queryClient);
          } else {
            invalidateAfterEntitlementPurchase(queryClient);
          }
          onPurchased?.();
        },
        onClosed,
      });
    } finally {
      setIsLaunching(false);
    }
  }, [descriptor, kind, sku, queryClient, user?.email, locale, onPurchased, onClosed]);

  return { available, isLaunching, startCheckout };
}
